import { PageHeader } from "@/components/PageHeader/PageHeader";
import Skeleton from "@/components/Skeleton/Skeleton";
import useAnalytics from "@/hooks/useAnalytics";
import useTranslation from "@/hooks/useTranslation";
import DeleteEpdAction from "@/pages/impact_references/epd/DeleteEpdAction";
import { useGetEpd, usePostEpd, useUpdateEpd } from "@/pages/impact_references/epd/useEpd";
import { DeleteOutlined } from "@ant-design/icons";
import { Button, Form, InputNumber, message, Space } from "antd";
import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DatasetDetails from "../DatasetDetails";
import styles from "./../ImpactReferenceDetails.module.scss";

const indicatorItems = [
  {
    label: "Acidification: terrestrial (TAP)",
    name: "acidification",
    errorLabel: "TAP",
    unit: "kg SO2-Eq",
    placeholder: "",
  },
  {
    label: "Ecotoxicity: freshwater (FETP)",
    name: "toxicity_freshwater",
    errorLabel: "FETP",
    unit: "kg 1,4-DCB-Eq",
    placeholder: "",
  },
  {
    label: "Ecotoxicity: marine (METP)",
    name: "toxicity_marine",
    errorLabel: "METP",
    unit: "kg 1,4-DCB-Eq",
    placeholder: "",
  },
  {
    label: "Ecotoxicity: terrestrial (TETP)",
    name: "toxicity_terrestrial",
    errorLabel: "TETP",
    unit: "kg 1,4-DCB-Eq",
    placeholder: "",
  },
  {
    label: "Eutrophication: freshwater (FEP)",
    name: "eutrophication_freshwater",
    errorLabel: "FEP",
    unit: "kg P-Eq",
    placeholder: "",
  },
  {
    label: "Eutrophication: marine (MEP)",
    name: "eutrophication_marine",
    errorLabel: "MEP",
    unit: "kg N-Eq",
    placeholder: "",
  },
  {
    label: "Fossil fuel resources (FFP)",
    name: "energy_resources",
    errorLabel: "FFP",
    unit: "kg oil-Eq",
    placeholder: "",
  },
  {
    label: "Global warming (GWP100)",
    name: "climate_change",
    errorLabel: "GWP100",
    unit: "kg CO2-Eq",
    placeholder: "",
  },
  {
    label: "Human toxicity: carcinogenic (HTPc)",
    name: "human_toxicity_carcinogenic",
    errorLabel: "HTPc",
    unit: "kg 1,4-DCB-Eq",
    placeholder: "",
  },
  {
    label: "Human toxicity: non-carcinogenic (HTPnc)",
    name: "human_toxicity_non_carcinogenic",
    errorLabel: "HTPnc",
    unit: "kg 1,4-DCB-Eq",
    placeholder: "",
  },
  {
    label: "Ionising radiation (IRP)",
    name: "ionising_radiation",
    errorLabel: "IRP",
    unit: "kBq Co-60-Eq",
    placeholder: "",
  },
  {
    label: "Land use (LOP)",
    name: "land_use",
    errorLabel: "LOP",
    unit: "m2*a crop-Eq",
    placeholder: "",
  },
  {
    label: "Material resources (SOP)",
    name: "material_resources",
    errorLabel: "SOP",
    unit: "kg Cu-Eq",
    placeholder: "",
  },
  {
    label: "Ozone depletion (ODPinfinite)",
    name: "ozone_depletion",
    errorLabel: "ODPinfinite",
    unit: "kg CFC-11-Eq",
    placeholder: "",
  },
  {
    label: "Particulate matter formation (PMFP)",
    name: "particulate_matter",
    errorLabel: "PMFP",
    unit: "kg PM2.5-Eq",
    placeholder: "",
  },
  {
    label: "Photochemical oxidant formation: human health (HOFP)",
    name: "photochemical_oxidant_human_health",
    errorLabel: "HOFP",
    unit: "kg NOx-Eq",
    placeholder: "",
  },
  {
    label: "Photochemical oxidant formation: terrestrial ecosystems (EOFP)",
    name: "photochemical_oxidant_terrestrial",
    errorLabel: "EOFP",
    unit: "kg NOx-Eq",
    placeholder: "",
  },
  {
    label: "Water use (WCP)",
    name: "water_use",
    errorLabel: "WCP",
    unit: "m3",
    placeholder: "",
  },
];

const EpdDetail = () => {
  const { referenceId } = useParams();
  const id = Number(referenceId);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const postEpd = usePostEpd();
  const updateEpd = useUpdateEpd();
  const translation = useTranslation();
  const analytics = useAnalytics();
  const location = useLocation();

  const { isInitialLoading: isLoading, data } = useGetEpd({ id });

  analytics.track("EPD Viewed");

  useEffect(() => {
    form.setFieldsValue(data);
  }, [data]);

  const referenceName = data?.activity_name || "Add EPD";

  const onSubmit = () => {
    form.validateFields().then((values) => {
      if (id) {
        updateEpd
          .mutateAsync({
            id: id,
            post: { impact_method: "ReCiPe 2016 v1.03, midpoint (H)", ...values },
          })
          .then(() => {
            analytics.track("EPD Updated");
            message.success(`EPD updated successfully`);
            if (location.key !== "default") {
              navigate(-1);
            } else {
              navigate(`/impact_references/epd`);
            }
          })
          .catch(() => {
            message.error(translation.errors.general);
          });
      } else {
        postEpd
          .mutateAsync({
            post: { impact_method: "ReCiPe 2016 v1.03, midpoint (H)", ...values },
          })
          .then(() => {
            analytics.track("EPD Created");
            message.success(`EPD created successfully`);
            navigate(`/impact_references/epd`);
          })
          .catch(() => {
            message.error(translation.errors.general);
          });
      }
    });
  };

  return (
    <div className={styles.ImpactReferenceDetails}>
      <PageHeader
        loading={isLoading}
        title={referenceName}
      />
      <Form
        form={form}
        layout="horizontal"
        labelAlign={"left"}
        labelWrap
        name="epdForm"
        colon={false}
        onFinish={onSubmit}
        autoComplete={"off"}
      >
        <div className={styles.form}>
          <div className={styles.left}>
            <DatasetDetails
              isLoading={isLoading}
              title={"EPD"}
              description={"Specific environmental product declaration impact data."}
            />
            <div className={styles.buttons}>
              <DeleteEpdAction
                id={id}
                onSuccess={() => navigate("/impact_references/epd")}
              >
                <Button
                  type={"link"}
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!id || isLoading}
                >
                  Delete EPD
                </Button>
              </DeleteEpdAction>

              <span>
                <Space>
                  <Button onClick={() => navigate(`/impact_references/epd`)}>Cancel</Button>
                  <Button
                    type={"primary"}
                    htmlType="submit"
                    disabled={isLoading || postEpd.isLoading || updateEpd.isLoading}
                    loading={postEpd.isLoading || updateEpd.isLoading}
                  >
                    Save
                  </Button>
                </Space>
              </span>
            </div>
          </div>
          <div className={styles.right}>
            <div className={styles.row}>
              <h2>Impact categories and indicators</h2>
            </div>
            <div className={styles.row}>
              <Space>
                <span className={styles.recipe_label}>Carbon footprint</span>
                <span className={styles.recipe_sub_label}>IPCC 2021</span>
              </Space>
            </div>
            <Form.Item
              label={"Global warming (GWP100)"}
              name={"ipcc_global_warming_potential"}
              style={{ marginBottom: 0 }}
              labelCol={{ flex: "300px" }}
              rules={[
                {
                  required: true,
                  message: `GWP100 is required.`,
                },
              ]}
            >
              {isLoading ? (
                <Skeleton
                  loading={isLoading}
                  style={{ width: 220, height: 14 }}
                ></Skeleton>
              ) : (
                <InputNumber
                  addonAfter={"kg CO2-Eq"}
                  style={{ maxWidth: 220, minWidth: 220 }}
                />
              )}
            </Form.Item>
            <div className={styles.row}>
              <Space>
                <span className={styles.recipe_label}>Environmental footprint</span>
                <span className={styles.recipe_sub_label}>
                  ReCiPe 2016 v1.03, midpoint (H) no LT
                </span>
              </Space>
            </div>
            {indicatorItems.map((item) => (
              <Form.Item
                key={item.name}
                label={item.label}
                name={item.name}
                style={{ marginBottom: 0 }}
                labelCol={{ flex: "300px" }}
                rules={[
                  {
                    required: true,
                    message: `${item.errorLabel} is required.`,
                  },
                ]}
              >
                {isLoading ? (
                  <Skeleton
                    loading={isLoading}
                    style={{ width: 220, height: 14 }}
                  ></Skeleton>
                ) : (
                  <InputNumber
                    addonAfter={item.unit}
                    style={{ maxWidth: 220, minWidth: 220 }}
                    placeholder={item.placeholder}
                  />
                )}
              </Form.Item>
            ))}
          </div>
        </div>
      </Form>
    </div>
  );
};

export default EpdDetail;
